import React, { useState, useMemo } from "react";
import { XYChart, LineSeries, AnimatedLineSeries, Axis, Tooltip, AreaSeries } from "@visx/xychart";
import { LinearGradient } from "@visx/gradient";
import { curveMonotoneX } from "@visx/curve";
import { withTooltip, TooltipWithBounds, defaultStyles } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import { useTranslations } from "use-intl";
export interface IDataPoint {
  year: number;
  revenue: number;
  expenses: number;
}
interface IBudgetEvolutionProps {
  data: IDataPoint[];
}
const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: "rgba(255,255,255,0.9)",
  color: "black",
  width: "200px"
};
export const BudgetEvolution: React.FC<IBudgetEvolutionProps> = ({
  data
}) => {
  const t = useTranslations();
  const accessors = {
    xAccessor: (d: IDataPoint) => d.year,
    yAccessorRevenue: (d: IDataPoint) => d.revenue,
    yAccessorExpenses: (d: IDataPoint) => d.expenses
  };
  const firstAndLastYears = useMemo(() => {
    const years = data.map(d => d.year);
    return [years[0], years[years.length - 1]];
  }, [data]);
  const customTickFormat = (tickValue: number) => {
    return firstAndLastYears.includes(tickValue) ? tickValue.toString() : "";
  };
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };
  const handleTooltip = (event: React.MouseEvent<SVGPathElement> | React.MouseEvent<SVGRectElement>, series: string, datum: string) => {
    const coords = localPoint(event) || {
      x: 0,
      y: 0
    };
    setHoveredSeries(series);
    return {
      tooltipLeft: coords.x,
      tooltipTop: coords.y,
      tooltipData: datum
    };
  };
  const formatNumberLocale = (number: number, locale = "ro-RO") => {
    return number.toLocaleString(locale);
  };
  return <>
      {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
      <style>
        {` .visx-tooltip { background: unset !important; padding: 0 !important; box-shadow: unset !important; } `}
      </style>
      <XYChart height={400} xScale={{
      type: "band",
      padding: 0,
      paddingInner: 0,
      round: true
    }} yScale={{
      type: "linear"
    }} data-sentry-element="XYChart" data-sentry-source-file="BudgetEvolution.tsx">
        <svg data-sentry-element="svg" data-sentry-source-file="BudgetEvolution.tsx">
          <defs data-sentry-element="defs" data-sentry-source-file="BudgetEvolution.tsx">
            <LinearGradient id="revenueGradient" from="green" to="green" fromOpacity={hoveredSeries === "revenue" ? 0.6 : 0.2} toOpacity={0} data-sentry-element="LinearGradient" data-sentry-source-file="BudgetEvolution.tsx" />
            <LinearGradient id="expensesGradient" from="red" to="red" fromOpacity={hoveredSeries === "expenses" ? 0.6 : 0.2} toOpacity={0} data-sentry-element="LinearGradient" data-sentry-source-file="BudgetEvolution.tsx" />
          </defs>
        </svg>
        <Axis orientation="bottom" tickValues={data.map(accessors.xAccessor)} tickFormat={customTickFormat} data-sentry-element="Axis" data-sentry-source-file="BudgetEvolution.tsx" />
        <Axis orientation="left" tickFormat={formatNumber} data-sentry-element="Axis" data-sentry-source-file="BudgetEvolution.tsx" />
        <AnimatedLineSeries dataKey="Revenue" data={data} curve={curveMonotoneX} xAccessor={accessors.xAccessor} yAccessor={accessors.yAccessorRevenue} stroke="green" strokeWidth={2} onMouseMove={event => handleTooltip(event, "Revenue", "revenue")} onMouseLeave={() => setHoveredSeries(null)} data-sentry-element="AnimatedLineSeries" data-sentry-source-file="BudgetEvolution.tsx" />
        <AnimatedLineSeries dataKey="Expenses" data={data} curve={curveMonotoneX} xAccessor={accessors.xAccessor} yAccessor={accessors.yAccessorExpenses} stroke="red" strokeWidth={2} onMouseMove={event => handleTooltip(event, "Expenses", "expenses")} onMouseLeave={() => setHoveredSeries(null)} data-sentry-element="AnimatedLineSeries" data-sentry-source-file="BudgetEvolution.tsx" />
        <AreaSeries dataKey="Revenue Fill" data={data} curve={curveMonotoneX} xAccessor={accessors.xAccessor} yAccessor={accessors.yAccessorRevenue} fill="url(#revenueGradient)" renderLine={false} data-sentry-element="AreaSeries" data-sentry-source-file="BudgetEvolution.tsx" />
        <AreaSeries dataKey="Expenses Fill" data={data} curve={curveMonotoneX} xAccessor={accessors.xAccessor} yAccessor={accessors.yAccessorExpenses} fill="url(#expensesGradient)" renderLine={false} data-sentry-element="AreaSeries" data-sentry-source-file="BudgetEvolution.tsx" />
        <Tooltip<{
        year: number;
        revenue: number;
        expenses: number;
      }> snapTooltipToDatumX snapTooltipToDatumY showVerticalCrosshair showSeriesGlyphs renderTooltip={({
        tooltipData
      }) => <div className="flex flex-col gap-2 rounded-lg bg-black/40 p-4 px-6 text-base text-white shadow-md backdrop-blur-md">
              {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
              <div>
                {t("year_label_20230802124573")}:{" "}
                <span className="font-normal">{tooltipData?.nearestDatum?.datum?.year}</span>
              </div>
              {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
              <div>
                {t("revenue_label_20230802124574")}:
                <span className="font-normal">
                  {formatNumberLocale(tooltipData?.nearestDatum?.datum?.revenue || 0)}
                </span>
              </div>
              {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
              <div>
                {t("expenses_label_20230802124575")}:
                <span className="font-normal">
                  {formatNumberLocale(tooltipData?.nearestDatum?.datum?.expenses || 0)}
                </span>
              </div>
            </div>} data-sentry-element="Tooltip" data-sentry-source-file="BudgetEvolution.tsx" />
      </XYChart>
    </>;
};
export default withTooltip(BudgetEvolution);